import { useSelector } from 'react-redux';
import { selectAppliedOffers } from '../../store/slices/OfferSlice';
import { selectGiftCard } from '../../store/slices/GiftCardSlice';
import { selectCachedOrder } from '../../store/slices/OrderSlice';
import { COUPON_TYPES } from '../../services/exports/Constants';

export type DiscountKind = 'offer' | 'promo' | 'gift' | 'points';

export interface DiscountExclusivityState {
  hasOffer: boolean;
  hasPromo: boolean;
  hasGift: boolean;
  hasPoints: boolean;
  /**
   * Which currently-applied discount would displace a NEW offer.
   * Full mutual exclusivity (PM video, 2026-07-31): a promo code, gift card OR a points
   * redemption all block an offer — exactly one discount applies per order.
   */
  offerBlockedBy: 'promo' | 'gift' | null;
}

/**
 * Derives the live discount-exclusivity state from Redux for the messaging surfaces.
 *
 * Model (PM video, supersedes the earlier "offer + points coexist" ruling): only ONE of
 * `{offer, promo, gift, points}` applies per order — each displaces the others. This hook
 * reports state only; the exclusivity engine (Inc 1) does the actual clearing.
 *
 * State sources:
 *  - offer  → `OfferSlice.appliedOffers`
 *  - promo  → `OrderSlice.cached_order.coupon_type === PROMO_CODE`
 *  - points → `OrderSlice.cached_order.coupon_type === CASHBACK` with redeemed points
 *  - gift   → `GiftCardSlice.item`
 *
 * `coupon_type` is a single field, so promo XOR points holds structurally.
 */
export default function useDiscountExclusivity(): DiscountExclusivityState {
  const appliedOffers = useSelector(selectAppliedOffers);
  const giftCard = useSelector(selectGiftCard);
  const cachedOrder = useSelector(selectCachedOrder);

  const hasOffer = (appliedOffers?.length ?? 0) > 0;
  const hasGift = Boolean(giftCard);
  const hasPromo = cachedOrder?.coupon_type === COUPON_TYPES.PROMO_CODE;
  const hasPoints = cachedOrder?.coupon_type === COUPON_TYPES.CASHBACK && (cachedOrder?.cashback_points ?? 0) > 0;

  // Only a promo code or gift card blocks an offer — an offer and points redemption coexist
  // (CTO-confirmed 2026-08-03).
  const offerBlockedBy: 'promo' | 'gift' | null = hasPromo ? 'promo' : hasGift ? 'gift' : null;

  return { hasOffer, hasPromo, hasGift, hasPoints, offerBlockedBy };
}
